import { useEffect, useRef, useState } from "react";
import { api, API, extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Copy, Plus, Trash2, Sparkles, Image as ImageIcon, Mic, X, Square, Crown, Zap, Code2, FileText, Clapperboard, Download, Globe, Upload, Lock, Plane, MessageSquare, ChevronDown, History, Mail, RotateCcw } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "@/i18n";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useAuth } from "@/context/AuthContext";
import { openCvForPrint } from "@/lib/cvPrint";
import { SPEED_TO_MODEL } from "@/components/ChatModeSelector";
import ChatModelDropdown from "@/components/ChatModelDropdown";
// (ChatIntegrationsPanel moved to dedicated /app/salesforce page)
import { DEFAULT_TEMPLATE_ID } from "@/lib/cvTemplates";
import CvTemplatePicker from "@/components/CvTemplatePicker";
import ChatMessageBubble from "@/components/ChatMessageBubble";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const DEFAULT_MODEL = SPEED_TO_MODEL.quick;
const TIER_RANK = { free: 0, creator: 1, pro: 2, power: 2, admin: 3 };
const planMeetsTier = (planId, required) =>
  (TIER_RANK[planId] ?? 0) >= (TIER_RANK[required] ?? 0);

const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8MB after base64 ~10MB JSON
const MAX_IMAGES_PER_TURN = 10; // Backend also caps at 10 — must stay in sync.

// Module-level cache of messages per session so the chat page can re-mount
// INSTANTLY when the user navigates back mid-stream. Without this, every
// remount of Chat.js fires a fresh GET /chat/sessions/:id/messages which
// blocks the UI behind a loading spinner — users perceive this as "the
// page isn't opening". With the cache, we render whatever we had before
// the unmount immediately, and just refresh from the server in the
// background so any newly-persisted assistant reply slides in.
const _chatMessageCache = new Map(); // sessionId → messages[]

// Decide whether the server snapshot of a session is "better" than what
// we have in memory. Used by the polling + remount-hydrate paths to
// avoid wiping a fresh partial-stream cache, while still adopting the
// final assistant reply once the backend bg_worker persists it.
function shouldAdoptServer(serverMsgs, currentMsgs) {
  const srv = serverMsgs || [];
  const curr = currentMsgs || [];
  if (srv.length > curr.length) return true;
  if (srv.length === 0) return false;
  // Same-length case is the tricky one. We compare the LAST message:
  //   - If server's last is assistant + non-empty AND our cache's last
  //     was still marked _streaming OR was shorter → server is fresher.
  //   - Same for any final non-streaming bubble that's now longer.
  const sl = srv[srv.length - 1];
  const cl = curr[curr.length - 1];
  if (!cl) return true;
  if (sl.role === "assistant" && cl.role === "assistant") {
    const wasStreaming = !!cl._streaming;
    const srvLen = (sl.content || "").length;
    const curLen = (cl.content || "").length;
    if (wasStreaming && srvLen > 0) return true;
    if (srvLen > curLen) return true;
  }
  return false;
}

export default function Chat() {
  const { t } = useT();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState(() => {
    // Persist the picked model across refreshes / app restarts.
    // Falls back to Quick (Gemini 3 Flash) if the stored value is unknown
    // or the user cleared storage.
    try {
      const stored = localStorage.getItem("smartai_chat_model");
      if (stored && Object.values(SPEED_TO_MODEL).includes(stored)) return stored;
    } catch { /* localStorage unavailable */ }
    return DEFAULT_MODEL;
  });
  // Thinking-effort dial (Fast / Balanced / Thorough). Persisted in
  // localStorage. Backend translates this into a system-prompt hint
  // that visibly shifts answer depth without changing latency baseline
  // for Fast.
  const [reasoningEffort, setReasoningEffort] = useState(() => {
    try {
      const stored = localStorage.getItem("lumioos_reasoning_effort");
      if (["fast", "balanced", "thorough"].includes(stored)) return stored;
    } catch { /* ignore */ }
    return "fast";
  });
  // Persist model whenever it changes so the next mount / refresh picks
  // up the user's choice instead of resetting to Quick.
  useEffect(() => {
    try { localStorage.setItem("smartai_chat_model", model); } catch { /* ignore quota */ }  }, [model]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Ref mirror of activeId so streaming callbacks (running long after the
  // user navigated to a different chat) can detect "I'm no longer the
  // visible session" and route their chunks into the per-session cache
  // instead of mutating the visible messages.
  const activeIdRef = useRef(null);
  // Per-session busy tracker — lets a user open a NEW chat while another
  // session is still generating its reply. Both run in parallel. The
  // global `busy` above governs the CURRENT session's input row only.
  const [busySessions, setBusySessions] = useState(new Set());
  const markSessionBusy = (sid, b) => {
    setBusySessions(prev => {
      const next = new Set(prev);
      if (b) next.add(sid); else next.delete(sid);
      return next;
    });
  };
  const navigate = useNavigate();
  // Modes: "text" (default chat), "image" (gpt-image-1), "code" (engineer mode), "cv" (CV builder), "video" (coming soon)
  const [mode, setMode] = useState("text");
  // Web search toggle — defaults ON so every chat answer is grounded in
  // fresh web data (Tavily) unless the user explicitly turns it off. The
  // user's choice persists in localStorage across sessions.
  const [webSearch, setWebSearchState] = useState(() => {
    const stored = localStorage.getItem("lumioos-web-search");
    return stored === null ? true : stored === "1";
  });
  const setWebSearch = (next) => {
    setWebSearchState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      localStorage.setItem("lumioos-web-search", value ? "1" : "0");
      return value;
    });
  };
  // CV template selection (applies to all CVs in this chat session)
  const [cvTemplateId, setCvTemplateId] = useState(() => {
    return localStorage.getItem("lumioos-cv-template") || DEFAULT_TEMPLATE_ID;
  });
  const handleCvTemplateChange = (tid) => {
    setCvTemplateId(tid);
    localStorage.setItem("lumioos-cv-template", tid);
  };
  // Feature flags — admin controls video button visibility/state from Admin panel
  const { isHidden, isComingSoon, flags } = useFeatureFlags();
  // GPT-5 access — Pro plan only (gated via the model dropdown; backend
  // double-checks based on the `model` field).
  const { user } = useAuth();
  const isPro = user?.plan === "pro" || user?.is_admin === true;
  const userPlan = user?.is_admin ? "admin" : (user?.plan || "free");

  // GPT-5 monthly usage — fetched ONLY when GPT-5 is the selected model.
  const [gpt5Usage, setGpt5Usage] = useState(null);
  useEffect(() => {
    if (model !== "gpt-5" || !isPro || isHidden("gpt5_smart_router")) {
      setGpt5Usage(null);
      return;
    }
    let alive = true;
    api.get("/me/gpt5-usage")
      .then((r) => { if (alive) setGpt5Usage(r.data); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
    // Re-fetch after each message send so the counter updates live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, isPro, messages.length]);

  // Claude Opus 4.5 (Master) monthly usage — fetched ONLY when Opus is selected.
  const [opusUsage, setOpusUsage] = useState(null);
  useEffect(() => {
    if (model !== "claude-opus-4-5-20251101" || !isPro || isHidden("opus_master")) {
      setOpusUsage(null);
      return;
    }
    let alive = true;
    api.get("/me/opus-usage")
      .then((r) => { if (alive) setOpusUsage(r.data); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [model, isPro, messages.length]);
  const videoHidden = isHidden("video_producer");
  const videoComingSoon = isComingSoon("video_producer");

  // Image attachments — supports up to MAX_IMAGES_PER_TURN images per message.
  // Each entry is { b64, preview } — b64 is what goes to the backend, preview
  // is the data URL used for the thumbnail chip.
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const scrollerRef = useRef(null);
  // Track whether the user has manually scrolled up while a message is
  // streaming in. When true, we STOP auto-scrolling so the user can read
  // earlier turns without being yanked back to the bottom every 100 ms.
  // Reset to false when the user reaches the bottom again or sends a new
  // message. `bottomLockedRef` = "the viewport is glued to the bottom".
  const bottomLockedRef = useRef(true);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  // Live abort controller for the current streaming request. Exposed via
  // ref so the "Stop" button can abort mid-stream without needing state.
  const abortCtrlRef = useRef(null);
  // Marks that the user intentionally stopped the stream — we swallow the
  // AbortError in the catch block instead of showing "err_generic".
  const userStoppedRef = useRef(false);
  // Session id of the currently streaming reply, so `stopStreaming` can
  // send POST /chat/stream/cancel/{sid} to also stop the server-side
  // LLM call (and stop burning credits) — not just the local fetch.
  const streamingSessionRef = useRef(null);

  const stopStreaming = () => {
    // 1) Tell the backend to cancel the LLM stream (server-side kill so
    //    we stop paying for tokens the user doesn't want).
    const sid = streamingSessionRef.current;
    if (sid) {
      const token = localStorage.getItem("smartai_token");
      fetch(`${API}/chat/stream/cancel/${sid}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { /* best-effort; local abort still runs */ });
    }
    // 2) Abort the local SSE fetch so we stop rendering incoming tokens.
    if (abortCtrlRef.current) {
      userStoppedRef.current = true;
      try { abortCtrlRef.current.abort(); } catch { /* noop */ }
    }
  };

  const continueStreaming = () => {
    // "Devam et" — DO NOT send a visible user message. Instead call
    // /chat/stream with { is_continuation: true, message: "" } so the
    // backend loads the previous stopped assistant reply, prepends a
    // "continue where you stopped" instruction internally, and merges
    // the new tokens back into that same bubble.
    if (busy) return;
    send("", { isContinuation: true });
  };

  const isNearBottom = () => {
    const el = scrollerRef.current;
    if (!el) return true;
    // 80 px slack accommodates padding + one extra streaming chunk.
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const scrollToBottom = (smooth = false) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (smooth && "scrollTo" in el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    bottomLockedRef.current = true;
    setShowJumpToBottom(false);
  };

  // Track user scroll intent. If they drift away from the bottom while a
  // response is streaming, unlock auto-scroll and reveal the jump-to-bottom
  // pill. If they scroll back to the bottom themselves, re-lock.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const near = isNearBottom();
      bottomLockedRef.current = near;
      setShowJumpToBottom(!near);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  // Marks an `activeId` change that happens BECAUSE we just created the
  // session mid-stream. The message-fetch effect uses this to skip the
  // server reload that would otherwise wipe the in-flight streaming bubble
  // and leave the user staring at an empty chat for 1-2 seconds.
  const justCreatedSessionRef = useRef(false);
  // Guards against a subtle cache-poisoning race: when the user clicks
  // session B while `messages` still holds session A's array (one render
  // before the load effect swaps it), the mirror-to-cache effect would
  // write A's messages into cache[B]. Later switching to B would then
  // render A's messages — which is exactly the "same page always opens"
  // bug users reported. We only mirror to cache when `messagesSessionRef`
  // matches the current activeId, meaning the load effect has confirmed
  // these messages actually belong to this session.
  const messagesSessionRef = useRef(null);

  // Upgrade gate state — shown when the backend returns 429 chat_daily_limit
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeMeta, setUpgradeMeta] = useState({ limit: 5, used: 5 });

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/chat/sessions");
      setSessions(data);
    } catch (e) {
      /* ignore */
    }
  };

  // Sync the input row's `busy` state with whichever session is currently
  // open. When the user switches tabs to a chat that's NOT generating,
  // the input goes live again so they can write a NEW question while
  // their first chat keeps streaming in the background. When they switch
  // BACK to the one that's still generating, busy goes true and Send
  // is disabled (we don't want a second send into the same session).
  useEffect(() => {
    if (!activeId) {
      // Empty state — Send is disabled by lack of input anyway.
      setBusy(false);
      return;
    }
    setBusy(busySessions.has(activeId));
  }, [activeId, busySessions]);

  useEffect(() => { loadSessions(); }, []);

  // Load the user's saved CV (if any) when they switch into CV mode. This
  // is what makes the "no more re-upload" UX work — the composer shows a
  // "CV kayıtlı" pill instead of the upload prompt whenever the backend
  // still has a CV on file. `loadSavedCv` is idempotent; refetching on
  // every mode toggle is cheap because it's a single-doc find_one.
  useEffect(() => {
    if (mode === "cv") {
      loadSavedCv();
    }
  }, [mode]);

  // When the chat page mounts, if the user has an in-flight AI reply
  // running in the background (started in this session and then they
  // navigated away), auto-select that session so they see the streaming
  // bubble immediately on return — no need to manually click the
  // conversation from the sidebar.
  const autoSelectAttemptedRef = useRef(false);
  useEffect(() => {
    if (autoSelectAttemptedRef.current) return;
    if (activeId) { autoSelectAttemptedRef.current = true; return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/chat/pending");
        if (cancelled) return;
        const ids = data?.session_ids || [];
        if (ids.length > 0) {
          autoSelectAttemptedRef.current = true;
          setActiveId(ids[0]);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  useEffect(() => {
    if (!activeId) { setMessages([]); messagesSessionRef.current = null; return; }
    // Skip the server reload when this `activeId` change came from the
    // current SSE stream (first user message creates a session id and we
    // adopt it; the streaming bubble is already correct locally).
    if (justCreatedSessionRef.current) {
      justCreatedSessionRef.current = false;
      messagesSessionRef.current = activeId;
      return;
    }
    // FAST PATH: if we have cached messages from a prior mount of this
    // session, render them INSTANTLY (zero spinner). Then do a silent
    // background re-fetch to merge in any new persisted reply that
    // landed while we were away. This is what lets the user click back
    // into chat mid-stream without staring at a loading spinner.
    const cached = _chatMessageCache.get(activeId);
    if (cached && cached.length > 0) {
      // The cached snapshot might include a streaming bubble whose
      // `_streaming` flag is still true (the unmount snapshotted it
      // mid-flight). The old streamer is dead — flip the flag off so
      // the cursor stops blinking; the in-flight server bg_worker will
      // deliver the full reply via the 2s poll and replace this partial
      // bubble entirely.
      setMessages(cached.map(m => m._streaming ? { ...m, _streaming: false } : m));
      messagesSessionRef.current = activeId;
      const requestedId = activeId;
      api.get(`/chat/sessions/${activeId}/messages`)
        .then((r) => {
          const srv = r.data || [];
          // Guard: only adopt if we're still on the same session. User
          // may have switched again before this GET resolved.
          if (activeIdRef.current !== requestedId) return;
          // Merge: only replace if server snapshot is actually fresher
          // (covers the same-length case where bg_worker finished and
          // saved a finalised assistant reply to MongoDB while our
          // cache still held the in-flight streaming bubble).
          setMessages(curr => shouldAdoptServer(srv, curr) ? srv : curr);
        })
        .catch(() => { /* keep cached version on transient errors */ });
      return;
    }
    // Clear stale messages before the network trip so the previous
    // session's bubbles don't briefly render under the new session's
    // header while the request is in flight.
    setMessages([]);
    setLoading(true);
    // Capture which session we're fetching for so a fast follow-up
    // click on session C can't overwrite the still-in-flight response
    // for session B once it lands. `activeIdRef` is updated by a
    // separate sync effect and reflects the CURRENT activeId (not the
    // stale one closed over by this effect).
    const requestedId = activeId;
    api.get(`/chat/sessions/${activeId}/messages`)
      .then((r) => {
        if (activeIdRef.current !== requestedId) return; // user switched — drop
        setMessages(r.data);
        messagesSessionRef.current = requestedId;
      })
      .catch(() => toast.error(t("err_generic")))
      .finally(() => setLoading(false));
  }, [activeId, t]);

  // Mirror every messages update into the module-level cache so the next
  // remount of this component can hydrate instantly. Gated on
  // `messagesSessionRef.current === activeId` — see the ref's declaration
  // for why (prevents session-A messages from poisoning cache[session-B]
  // during the one-render window after setActiveId(B)).
  useEffect(() => {
    if (
      activeId &&
      messages &&
      messages.length > 0 &&
      messagesSessionRef.current === activeId
    ) {
      _chatMessageCache.set(activeId, messages);
    }
  }, [activeId, messages]);

  // Keep activeIdRef in sync with state — streaming callbacks read this
  // to detect "user navigated away" and reroute chunks into the cache.
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // Background-stream auto-refresh: if this session has an in-flight AI
  // reply running on the server (user opened the chat from another tab
  // mid-stream), poll for messages every 2s and inject a "AI is typing…"
  // placeholder at the end so the user knows something is happening even
  // before the final assistant message arrives. As soon as the assistant
  // message lands in DB, polling stops and the placeholder is replaced.
  const [serverPendingThisSession, setServerPendingThisSession] = useState(false);
  useEffect(() => {
    if (!activeId) { setServerPendingThisSession(false); return; }
    let cancelled = false;
    let pollTimer = null;
    const checkAndPoll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/chat/pending");
        const isPending = (data?.session_ids || []).includes(activeId);
        if (cancelled) return;
        setServerPendingThisSession(prev => {
          // Edge case: when pending transitions true → false, the server
          // JUST finished bg_worker and saved the final assistant reply.
          // We must do one last fetch — otherwise the UI is stuck on the
          // partial cached bubble forever.
          if (prev && !isPending) {
            api.get(`/chat/sessions/${activeId}/messages`)
              .then((r) => {
                if (!cancelled) {
                  const srv = r.data || [];
                  setMessages((current) => shouldAdoptServer(srv, current) ? srv : current);
                }
              })
              .catch(() => {});
          }
          return isPending;
        });
        if (isPending) {
          // Re-fetch session messages so any newly persisted assistant
          // reply appears without a manual refresh. shouldAdoptServer
          // decides when the server snapshot is genuinely fresher than
          // our cached streaming bubble (handles the same-length finalised
          // vs partial-streaming case correctly).
          try {
            const r = await api.get(`/chat/sessions/${activeId}/messages`);
            if (!cancelled) {
              const serverMsgs = r.data || [];
              setMessages((current) => shouldAdoptServer(serverMsgs, current) ? serverMsgs : current);
            }
          } catch { /* ignore */ }
          // Keep polling while the stream is in flight.
          pollTimer = setTimeout(checkAndPoll, 2_000);
        }
      } catch {
        // On error, back off but try again so transient blips don't
        // leave the user stuck on the placeholder forever.
        if (!cancelled) pollTimer = setTimeout(checkAndPoll, 4_000);
      }
    };
    checkAndPoll();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [activeId]);

  useEffect(() => {
    // Auto-scroll ONLY while the user is glued to the bottom. If they
    // scrolled up (bottomLockedRef=false), respect that and let them
    // read older turns while the stream continues below.
    if (!bottomLockedRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    // Use requestAnimationFrame so the scroll happens AFTER the browser
    // has laid out the new streamed content — this prevents the jittery,
    // step-by-step "kasma" effect that happens when we set scrollTop on
    // every single token during streaming.
    const raf = requestAnimationFrame(() => {
      // Only touch scrollTop if we're actually not at the bottom already
      // to skip redundant layout thrash.
      const diff = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (diff > 1) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, busy]);

  const startNew = () => { setActiveId(null); setMessages([]); setInput(""); clearImages(); clearCvUpload(); };

  const clearImages = () => { setImages([]); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const removeImageAt = (idx) => { setImages((imgs) => imgs.filter((_, i) => i !== idx)); };

  // CV mode upload — extracted text from a PDF/DOCX/TXT or image OCR of the
  // user's existing CV. Sent to backend on the next chat turn so the model
  // can run "FLOW A" (review existing CV → ask target role → tailor).
  const [cvUploadText, setCvUploadText] = useState(null);
  const [cvUploadName, setCvUploadName] = useState(null);
  const [cvUploadBusy, setCvUploadBusy] = useState(false);
  // Persistent CV that lives on the user's account (saved from a previous
  // upload). Populated by GET /api/cv/current whenever the user enters CV
  // mode. When present, the composer shows a "CV kayıtlı" pill instead of
  // the "Upload your CV" prompt — no more re-uploading on every session.
  const [savedCv, setSavedCv] = useState(null); // { filename, format, saved_at, ... }
  const [savedCvLoading, setSavedCvLoading] = useState(false);
  // Downloading the current-session assistant CV as a PDF. Kept in state so
  // the "PDF İndir" button can show a spinner while reportlab renders.
  const [cvPdfBusy, setCvPdfBusy] = useState(false);
  const cvFileInputRef = useRef(null);
  const clearCvUpload = () => {
    setCvUploadText(null);
    setCvUploadName(null);
    if (cvFileInputRef.current) cvFileInputRef.current.value = "";
  };

  const loadSavedCv = async () => {
    setSavedCvLoading(true);
    try {
      const r = await api.get("/cv/current");
      if (r.data?.has_cv) {
        setSavedCv(r.data);
      } else {
        setSavedCv(null);
      }
    } catch {
      // Non-fatal — user just sees the upload prompt.
      setSavedCv(null);
    } finally {
      setSavedCvLoading(false);
    }
  };

  const deleteSavedCv = async () => {
    const ok = window.confirm(t("cv_saved_delete_confirm") || "Delete your saved CV? You'll need to upload it again next time.");
    if (!ok) return;
    try {
      await api.delete("/cv/current");
      setSavedCv(null);
      toast.success(t("cv_saved_deleted") || "Saved CV deleted");
    } catch {
      toast.error(t("err_generic"));
    }
  };

  // Download the LAST assistant CV in the current chat as a server-rendered
  // PDF (via reportlab). Falls back to the legacy client-side print flow
  // when no assistant markdown is available yet.
  const downloadLatestCvAsPDF = async () => {
    // Find the last assistant message with markdown-looking content.
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && (m.content || "").trim().length > 0);
    if (!lastAssistant) {
      toast.info(t("cv_pdf_no_content") || "Send a message first so Lumioos can draft your CV.");
      return;
    }
    setCvPdfBusy(true);
    try {
      const token = localStorage.getItem("smartai_token");
      const resp = await fetch(`${API}/cv/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          markdown: lastAssistant.content,
          filename: `lumioos_cv_${Date.now()}.pdf`,
        }),
      });
      if (!resp.ok) throw new Error("PDF generation failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lumioos_cv_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(t("cv_pdf_ready") || "PDF ready");
    } catch (e) {
      toast.error(t("cv_pdf_failed") || "Could not generate PDF");
    } finally {
      setCvPdfBusy(false);
    }
  };

  // Ask the user for a target job description in a native prompt (keeps
  // the diff surgical vs. a full modal) then hit /cv/ats-score.
  const runAtsScore = async () => {
    const jd = window.prompt(
      t("cv_ats_prompt") || "Paste the target job description (min 20 chars):",
      ""
    );
    if (!jd || jd.trim().length < 20) {
      if (jd !== null) toast.error(t("cv_ats_too_short") || "Please paste a longer job description");
      return;
    }
    setCvPdfBusy(true);
    try {
      const r = await api.post("/cv/ats-score", { job_description: jd.trim() });
      const d = r.data || {};
      // Render the score summary as an assistant message so it lives in
      // the chat transcript.
      const md = [
        `## ${t("cv_ats_result_title") || "ATS Score Result"}: ${d.overall_score ?? "?"}/100`,
        d.verdict ? `> ${d.verdict}` : "",
        "",
        `**${t("cv_ats_categories") || "Categories"}:**`,
        ...Object.entries(d.categories || {}).map(([k, v]) => `- ${k}: ${v}/100`),
        "",
        d.matched_keywords?.length ? `**${t("cv_ats_matched") || "Matched keywords"}:** ${d.matched_keywords.join(", ")}` : "",
        d.missing_keywords?.length ? `**${t("cv_ats_missing") || "Missing keywords"}:** ${d.missing_keywords.join(", ")}` : "",
        "",
        d.recommendations?.length ? `**${t("cv_ats_recommendations") || "Recommendations"}:**` : "",
        ...(d.recommendations || []).map((r) => `- ${r}`),
      ].filter(Boolean).join("\n");
      const localMsg = {
        id: `local-ats-${Date.now()}`,
        role: "assistant",
        content: md,
        created_at: new Date().toISOString(),
      };
      setMessages((m) => [...m, localMsg]);
      toast.success(t("cv_ats_ready") || "ATS score ready");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || t("err_generic"));
    } finally {
      setCvPdfBusy(false);
    }
  };

  const runOptimize = async () => {
    const jd = window.prompt(
      t("cv_optimize_prompt") || "Paste the target job description (or leave empty and give an instruction):",
      ""
    );
    if (jd === null) return;
    const instruction = !jd.trim() ? window.prompt(
      t("cv_optimize_instruction") || "How should Lumioos optimize your CV?",
      ""
    ) : "";
    if (!jd.trim() && (instruction === null || !instruction.trim())) {
      toast.error(t("cv_optimize_need_input") || "Provide either a job description or an instruction");
      return;
    }
    setCvPdfBusy(true);
    try {
      const r = await api.post("/cv/optimize", {
        job_description: jd?.trim() || null,
        instruction: (instruction || "").trim() || null,
      });
      const md = r.data?.markdown || "";
      if (!md) {
        toast.error(t("cv_optimize_empty") || "No CV returned");
        return;
      }
      const localMsg = {
        id: `local-opt-${Date.now()}`,
        role: "assistant",
        content: `## ${t("cv_optimize_result_title") || "Optimized CV"}\n\n${md}`,
        created_at: new Date().toISOString(),
      };
      setMessages((m) => [...m, localMsg]);
      toast.success(t("cv_optimize_ready") || "Optimized CV ready");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || t("err_generic"));
    } finally {
      setCvPdfBusy(false);
    }
  };

  // ============ CV Version History (Phase 3) ============
  // Modal state + fetched versions list. Rather than a permanently-mounted
  // panel we open a lightweight dialog on demand — keeps the composer
  // uncluttered for users who never touch history.
  const [cvHistoryOpen, setCvHistoryOpen] = useState(false);
  const [cvHistoryLoading, setCvHistoryLoading] = useState(false);
  const [cvHistoryVersions, setCvHistoryVersions] = useState([]);
  const [cvRestoring, setCvRestoring] = useState(null); // index being restored

  const openCvHistory = async () => {
    setCvHistoryOpen(true);
    setCvHistoryLoading(true);
    try {
      const r = await api.get("/cv/versions");
      setCvHistoryVersions(r.data?.versions || []);
    } catch {
      toast.error(t("err_generic"));
      setCvHistoryVersions([]);
    } finally {
      setCvHistoryLoading(false);
    }
  };

  const restoreCvVersion = async (index) => {
    setCvRestoring(index);
    try {
      await api.post("/cv/restore", { index });
      toast.success(t("cv_history_restored") || "Version restored");
      // Refresh both the composer chip and the modal list.
      await Promise.all([loadSavedCv(), (async () => {
        const r = await api.get("/cv/versions");
        setCvHistoryVersions(r.data?.versions || []);
      })()]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || t("err_generic"));
    } finally {
      setCvRestoring(null);
    }
  };

  // ============ AI Cover Letter (Phase 3) ============
  // Modal that collects JD + optional company/role/tone, hits
  // /api/cv/cover-letter, then renders the letter as an assistant message
  // in the transcript (so it inherits the existing copy/regen affordances).
  const [coverOpen, setCoverOpen] = useState(false);
  const [coverJd, setCoverJd] = useState("");
  const [coverCompany, setCoverCompany] = useState("");
  const [coverRole, setCoverRole] = useState("");
  const [coverTone, setCoverTone] = useState("confident");
  const [coverBusy, setCoverBusy] = useState(false);
  // Cache the last generated letter so the drawer keeps a "PDF olarak
  // indir" affordance after the initial toast. Cleared when the drawer
  // reopens with a fresh JD.
  const [coverLastMd, setCoverLastMd] = useState("");
  const [coverPdfBusy, setCoverPdfBusy] = useState(false);

  const runCoverLetter = async () => {
    if (coverJd.trim().length < 20) {
      toast.error(t("cv_cover_jd_too_short") || "Please paste a longer job description");
      return;
    }
    setCoverBusy(true);
    try {
      const r = await api.post("/cv/cover-letter", {
        job_description: coverJd.trim(),
        company_name: coverCompany.trim() || null,
        role_title: coverRole.trim() || null,
        tone: coverTone.trim() || null,
        lang: null, // auto (backend defaults to CV language)
      });
      const md = r.data?.markdown || "";
      if (!md) {
        toast.error(t("cv_cover_empty") || "No cover letter returned");
        return;
      }
      const localMsg = {
        id: `local-cover-${Date.now()}`,
        role: "assistant",
        content: md,
        created_at: new Date().toISOString(),
      };
      setMessages((m) => [...m, localMsg]);
      setCoverLastMd(md);
      toast.success(t("cv_cover_ready") || "Cover letter ready");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || t("err_generic"));
    } finally {
      setCoverBusy(false);
    }
  };

  const downloadCoverLetterPdf = async () => {
    if (!coverLastMd) return;
    setCoverPdfBusy(true);
    try {
      const token = localStorage.getItem("smartai_token");
      const suffix = (coverCompany.trim() || coverRole.trim() || "cover_letter")
        .replace(/[^a-z0-9]+/gi, "_")
        .toLowerCase();
      const resp = await fetch(`${API}/cv/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          markdown: coverLastMd,
          filename: `lumioos_${suffix}.pdf`,
        }),
      });
      if (!resp.ok) throw new Error("PDF generation failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lumioos_${suffix}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(t("cv_cover_pdf_ready") || "Cover letter PDF ready");
    } catch {
      toast.error(t("cv_pdf_failed") || "Could not generate PDF");
    } finally {
      setCoverPdfBusy(false);
    }
  };

  // ============ CV Diff View (Phase 3) ============
  // Simple line-level diff between two versions. Uses the `diff` package
  // that's already in node_modules. State holds the two version indices
  // being compared and the pre-computed diff blocks (added/removed/same).
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffFromIdx, setDiffFromIdx] = useState(null);
  const [diffToIdx, setDiffToIdx] = useState(null);
  const [diffFromLabel, setDiffFromLabel] = useState("");
  const [diffToLabel, setDiffToLabel] = useState("");
  const [diffFromText, setDiffFromText] = useState("");
  const [diffToText, setDiffToText] = useState("");
  const [diffBlocks, setDiffBlocks] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);

  const openDiff = async (targetIdx) => {
    // Compare `targetIdx` (a history version) against the current active
    // CV (index 0). Fetches full text on demand (versions listing sends
    // only previews to keep the initial payload light).
    setDiffLoading(true);
    setDiffOpen(true);
    setDiffFromIdx(0);
    setDiffToIdx(targetIdx);
    try {
      const r = await api.get("/cv/versions?full=true");
      const versions = r.data?.versions || [];
      const cur = versions.find((v) => v.index === 0);
      const tgt = versions.find((v) => v.index === targetIdx);
      const fromText = cur?.preview || "";
      const toText = tgt?.preview || "";
      setDiffFromLabel(cur?.filename || "Current");
      setDiffToLabel(tgt?.filename || `v${targetIdx}`);
      setDiffFromText(fromText);
      setDiffToText(toText);

      // Dynamic import so the diff lib only loads when the user actually
      // opens the diff view.
      const Diff = await import("diff");
      const blocks = Diff.diffLines(fromText, toText, { newlineIsToken: false });
      setDiffBlocks(blocks);
    } catch {
      toast.error(t("err_generic"));
      setDiffBlocks([]);
    } finally {
      setDiffLoading(false);
    }
  };

  const handleCvUploadFile = async (file) => {
    if (!file) return;
    // 8 MB hard cap mirrors backend. Friendly toast instead of letting the
    // backend reject with a 413.
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("cv_upload_too_large") || "File too large (max 8 MB)");
      return;
    }
    setCvUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/cv/parse-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Image uploads come back with `image_b64` instead of text — for those
      // we attach to the chat as a vision input so the LLM OCRs it.
      if (r.data.format === "image" && r.data.image_b64) {
        // CV image upload — replace any existing images (a CV OCR pass is a
        // single-image workflow). This preserves the previous single-image
        // semantic for the CV upload path.
        setImages([{
          b64: r.data.image_b64,
          preview: `data:${r.data.mime || "image/jpeg"};base64,${r.data.image_b64}`,
        }]);
        setCvUploadName(file.name);
        toast.success(t("cv_upload_image_ok") || "CV image attached — Lumioos will read it on next message");
      } else {
        setCvUploadText(r.data.text || "");
        setCvUploadName(file.name);
        // Refresh the "savedCv" state — parse-upload now persists on the
        // backend, so the user sees the saved-CV pill immediately.
        if (r.data.saved) {
          loadSavedCv();
        }
        toast.success(t("cv_upload_ok") || `CV loaded: ${file.name}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : detail?.message) || t("cv_upload_failed") || "Could not read this file";
      toast.error(msg);
    } finally {
      setCvUploadBusy(false);
      if (cvFileInputRef.current) cvFileInputRef.current.value = "";
    }
  };

  // Drag-and-drop state for visual feedback while the user is dragging
  // a file over the chat area.
  const [dragHover, setDragHover] = useState(false);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const dragDepthRef = useRef(0); // counter to handle nested dragenter/leave

  const handleImageFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("chat_only_image")); return; }
    if (file.size > MAX_IMAGE_BYTES) { toast.error(t("chat_image_too_large")); return; }
    // Enforce the per-turn cap. Reading `images.length` off the closure would
    // be stale if the user picks 3 files at once (each reader fires async),
    // so we check inside the setter using the freshest state.
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target.result;
      const b64 = result.split(",")[1];
      setImages((prev) => {
        if (prev.length >= MAX_IMAGES_PER_TURN) {
          toast.error(t("chat_image_max") || `Max ${MAX_IMAGES_PER_TURN} images per message`);
          return prev;
        }
        return [...prev, { b64, preview: result }];
      });
    };
    reader.readAsDataURL(file);
  };

  const onImagePick = (e) => {
    const files = Array.from(e.target.files || []);
    // Trim upfront to avoid firing readers we'll only discard afterwards.
    const room = Math.max(0, MAX_IMAGES_PER_TURN - images.length);
    if (files.length > room) {
      toast.error(t("chat_image_max") || `Max ${MAX_IMAGES_PER_TURN} images per message`);
    }
    files.slice(0, room).forEach((f) => handleImageFile(f));
    // Reset the input so picking the same file twice still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag-and-drop handlers for the chat area. We use a depth counter because
  // dragenter/dragleave fire for every child element as the cursor moves over
  // them — without the counter the overlay would flicker.
  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragHover(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragHover(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragHover(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImageFile(file);
  };

  // Allow pasting images (Cmd/Ctrl+V) directly into the chat
  const onPasteFromClipboard = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          handleImageFile(file);
          break;
        }
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribe(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(t("chat_mic_denied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      const { data } = await api.post("/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data?.text || "").trim();
      if (!text) { toast.error(t("chat_voice_empty")); return; }
      setInput((cur) => (cur ? cur + " " + text : text));
      toast.success(t("chat_voice_added"));
    } catch (err) {
      toast.error(extractErr(err, t("err_generic")));
    } finally {
      setTranscribing(false);
    }
  };

  const send = async (textOverride, opts = {}) => {
    const isContinuation = !!opts.isContinuation;
    const text = (textOverride ?? input).trim();
    const hasImages = images.length > 0;
    // Continuation clicks send an empty message on purpose — skip the
    // "you must type something" guard for that path.
    if (!isContinuation && !text && !hasImages) { toast.error(t("err_message_required")); return; }
    // Snapshot the session that this send belongs to BEFORE we await anything.
    // The user may switch to another chat mid-stream — busy state must
    // follow the original session, not the new one. We use `let` because
    // for brand-new sessions the real id arrives via the SSE `meta` event,
    // and the finally block needs to clear the CORRECT session id.
    let sendingSessionId = activeId || "pending-new";
    markSessionBusy(sendingSessionId, true);
    setBusy(true);
    const sendText = text || t("chat_describe_image");
    // Snapshot the images before clearing state so the async request keeps
    // the exact set the user just sent even if they immediately start typing
    // + attaching for the NEXT turn.
    const attachedImagesB64 = images.map((im) => im.b64);
    const attachedPreview = images[0]?.preview || null;
    const attachedCount = images.length;
    // Snapshot the CV upload text BEFORE clearing — it goes out with this
    // turn only. Follow-up turns reuse the conversation memory; sending it
    // again would re-prime the system prompt and waste tokens.
    const cvUploadSnapshot = mode === "cv" ? cvUploadText : null;
    setInput("");
    clearImages();
    if (cvUploadSnapshot) clearCvUpload();

    // -------- IMAGE GENERATION MODE -------- //
    if (mode === "image" && !attachedCount) {
      const optimistic = {
        id: `tmp-${Date.now()}`,
        role: "user",
        content: `🎨 ${sendText}`,
        created_at: new Date().toISOString(),
      };
      setMessages((m) => [...m, optimistic]);
      try {
        const { data } = await api.post("/chat/generate-image", {
          session_id: activeId,
          prompt: sendText,
          quality: "medium",
        });
        const wasNew = !activeId;
        if (wasNew) justCreatedSessionRef.current = true;
        setActiveId(data.session_id);
        setMessages((m) => {
          const without = m.filter((x) => x.id !== optimistic.id);
          return [...without, data.user_message, data.assistant_message];
        });
        if (wasNew) loadSessions();
      } catch (err) {
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        setInput(text);
        const detail = err?.response?.data?.detail;
        if (typeof detail === "object" && detail?.code === "image_locked") {
          setUpgradeMeta({ limit: 0, used: 0 });
          setShowUpgrade(true);
        } else if (typeof detail === "object" && detail?.code === "image_quota_exceeded") {
          toast.error(detail.message);
        } else {
          toast.error(typeof detail === "string" ? detail : t("err_generic"));
        }
      } finally {
        setBusy(false);
        markSessionBusy(sendingSessionId, false);
      }
      return;
    }

    // -------- VIDEO MODE (gated — Coming Soon when flag is set) -------- //
    if (mode === "video" && !attachedCount) {
      toast.info(videoComingSoon
        ? (t("chat_video_coming") || "Video generation is coming soon!")
        : (t("chat_video_unavailable") || "Video generation isn't enabled yet — admin must configure the provider."));
      setInput(text);
      setBusy(false);
      markSessionBusy(sendingSessionId, false);
      return;
    }

    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: attachedCount === 0
        ? sendText
        : attachedCount === 1
          ? `[image attached] ${sendText}`
          : `[${attachedCount} images attached] ${sendText}`,
      created_at: new Date().toISOString(),
      preview_image: attachedPreview,
    };
    // Streaming assistant placeholder — appended right after the user msg so the
    // bubble grows token-by-token (like ChatGPT). Replaced by the final
    // assistant_message from the `done` SSE event.
    const streamId = `stream-${Date.now()}`;
    // For continuation we also remember the id of the previous stopped
    // bubble so the `done` handler can DELETE it (its content will be
    // merged into the new bubble by the backend before we render it).
    let prevStoppedId = null;
    if (isContinuation) {
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") prevStoppedId = last.id;
    }
    const streamingPlaceholder = {
      id: streamId,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
      model: model,
      _streaming: true,
    };
    if (isContinuation) {
      // Strip the ⏹ marker off the previous stopped bubble AND append a
      // fresh streaming placeholder BELOW it. Tokens will flow into the
      // new placeholder; the `done` event will then merge both bubbles
      // into one final message and delete the stopped one.
      setMessages((m) => {
        const cleaned = m.map((x) => {
          if (x.id !== prevStoppedId) return x;
          const withoutMarker = (x.content || "")
            .replace(/\n*_?⏹[^_\n]*_?\s*$/u, "")
            .trimEnd();
          return { ...x, content: withoutMarker, _streaming: false };
        });
        return [...cleaned, streamingPlaceholder];
      });
    } else {
      setMessages((m) => [...m, optimistic, streamingPlaceholder]);
    }
    // Optimistic Sidebar badge bump — appears INSTANTLY instead of waiting
    // for the next /chat/pending poll. Backend confirms within 1.5s.
    try {
      window.dispatchEvent(new CustomEvent("lumioos:chat-pending-bump", { detail: { delta: 1 } }));
    } catch { /* DOM-less env */ }
    try {
      const token = localStorage.getItem("smartai_token");
      // Safety watchdog: if no delta arrives within 90s, abort so the UI never
      // gets stuck in a "busy" state with no escape hatch.
      const abortCtrl = new AbortController();
      abortCtrlRef.current = abortCtrl;
      userStoppedRef.current = false;
      const watchdog = setTimeout(() => abortCtrl.abort(), 90000);
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        signal: abortCtrl.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: activeId,
          message: sendText,
          model,
          // Send as list so backend can process multiple images. First image
          // duplicated in `image_base64` too for any old backend deploy that
          // hasn't picked up `image_base64_list` yet — backend dedupes.
          image_base64: attachedImagesB64[0] || null,
          image_base64_list: attachedImagesB64.length > 0 ? attachedImagesB64 : null,
          mode: mode === "code" || mode === "cv" || mode === "travel" ? mode : "text",
          // null → backend uses heuristic; true → force Tavily; false reserved.
          web_search: webSearch ? true : null,
          // CV mode only: parsed text from the uploaded CV (PDF/DOCX/TXT).
          // Backend injects it into the system prompt and only on first turn
          // — we clear after first send so follow-ups don't re-pay the cost.
          cv_upload_text: mode === "cv" ? (cvUploadSnapshot || null) : null,
          reasoning_effort: reasoningEffort,
          is_continuation: isContinuation,
        }),
      });
      if (!resp.ok) {
        // Try to parse error JSON. FastAPI returns {"detail": ...}
        let detail = null;
        try { const j = await resp.json(); detail = j?.detail; } catch {}
        const err = new Error("Request failed");
        err.response = { status: resp.status, data: { detail } };
        throw err;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalDone = null;
      let streamError = null;

      // Smooth-stream buffer: accumulate token chunks in a ref-style closure
      // and flush to React state at most once per animation frame (~60fps).
      // This prevents the "kasıyor" (jittery/choppy) feel that happens when
      // every single delta triggers a full message-list re-render. Combined
      // with React.memo on ChatMessageBubble, only the streaming bubble
      // actually re-renders — older messages skip their work entirely.
      let pendingText = "";
      let flushScheduled = false;
      const flushPending = () => {
        if (!pendingText) { flushScheduled = false; return; }
        const chunk = pendingText;
        pendingText = "";
        flushScheduled = false;
        // If the user has switched to a DIFFERENT session while this
        // stream was still running, write the chunk into the per-session
        // message cache (so it's there when they navigate back) but
        // DON'T mutate the visible `messages` state — that belongs to
        // whichever session is currently open. Without this, Q1's
        // tokens would visibly leak into the Q2 chat the user just
        // opened.
        if (sendingSessionId !== "pending-new" && sendingSessionId !== activeIdRef.current) {
          const cached = _chatMessageCache.get(sendingSessionId) || [];
          const updated = cached.map((x) => x.id === streamId
            ? { ...x, content: (x.content || "") + chunk }
            : x);
          _chatMessageCache.set(sendingSessionId, updated);
          return;
        }
        setMessages((m) => m.map((x) => x.id === streamId
          ? { ...x, content: (x.content || "") + chunk }
          : x));
      };
      const scheduleFlush = () => {
        if (flushScheduled) return;
        flushScheduled = true;
        // rAF syncs to next paint AND auto-pauses when the tab is hidden,
        // saving wasted renders during background streams. Fallback to
        // setTimeout for headless environments (SSR / Node tests).
        if (typeof window !== "undefined" && window.requestAnimationFrame) {
          window.requestAnimationFrame(flushPending);
        } else {
          setTimeout(flushPending, 16);
        }
      };

      const handleEvent = (eventName, dataStr) => {
        let data = {};
        try { data = JSON.parse(dataStr); } catch { return; }
        if (eventName === "meta") {
          // Remember the session id so the Durdur button can hit the
          // server-side cancel endpoint (which stops the LLM stream and
          // saves the credits we'd otherwise burn generating tokens
          // the user doesn't want).
          if (data.session_id) streamingSessionRef.current = data.session_id;
          if (data.session_id && !activeId) {
            // INSTANT SIDEBAR UPDATE: prepend the new session to the
            // sidebar list right when the server tells us its id, so the
            // user sees their new conversation appear in the history
            // immediately — even if they switch to another chat or open
            // a new one before this stream finishes. Without this the
            // list only refreshed after the `done` event (end of stream),
            // which can be 10-30s later for long replies.
            const optimisticTitle = sendText.length > 48
              ? sendText.slice(0, 48).trimEnd() + "…"
              : sendText;
            setSessions((s) => {
              if (s.some((x) => x.id === data.session_id)) return s;
              const nowIso = new Date().toISOString();
              return [{
                id: data.session_id,
                title: optimisticTitle,
                created_at: nowIso,
                updated_at: nowIso,
              }, ...s];
            });
            // Background-refresh so the canonical title (server-generated
            // via _generate_session_title) replaces our optimistic one.
            loadSessions();

            // Only yank the user's active chat to this new session if
            // they're STILL on the empty-new-chat screen. If they've
            // already navigated away (e.g. clicked another chat or
            // pressed "New chat" again), let this stream keep running
            // in the background — its tokens are routed to the cache.
            if (activeIdRef.current === null) {
              justCreatedSessionRef.current = true;
              setActiveId(data.session_id);
            }
            // Transfer the "pending-new" busy marker we set in send() to
            // the real session id now that the server told us what it
            // is. Without this, switching to this new session later
            // would think it's idle even though it's still streaming.
            if (sendingSessionId === "pending-new") {
              markSessionBusy(data.session_id, true);
              markSessionBusy("pending-new", false);
              sendingSessionId = data.session_id;
            }
          }
        } else if (eventName === "delta") {
          pendingText += (data.text || "");
          scheduleFlush();
        } else if (eventName === "done") {
          // Ensure any buffered text lands before we replace with the final msg
          flushPending();
          finalDone = data;
        } else if (eventName === "error") {
          flushPending();
          streamError = data.message || "stream error";
        }
      };

      // Read SSE stream line-by-line. Events are separated by a blank line and
      // each event has `event: name\ndata: jsonstring`.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        // Reset watchdog each time data arrives so a long but progressing
        // stream doesn't get killed.
        clearTimeout(watchdog);
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = raw.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
          }
          if (dataStr) handleEvent(eventName, dataStr);
        }
      }

      if (streamError) throw new Error(streamError);
      if (!finalDone) throw new Error("Stream ended without 'done' event");

      const data = finalDone;
      const wasNew = !activeId;
      if (wasNew) justCreatedSessionRef.current = true;
      // If user has switched to a different session while this stream
      // ran, update the cache directly so the final message will appear
      // when they return — don't replace the visible `messages` (which
      // belongs to the currently-open session).
      const finalSessionId = data.session_id;
      if (sendingSessionId !== "pending-new" && finalSessionId !== activeIdRef.current) {
        const cached = _chatMessageCache.get(finalSessionId) || [];
        if (isContinuation) {
          // Drop the stopped bubble AND the streaming placeholder, keep
          // everything else, then append the merged assistant_message
          // (backend gives it the same id as the original stopped row so
          // subsequent reloads stay stable).
          const cleaned = cached.filter((x) => x.id !== prevStoppedId && x.id !== streamId);
          _chatMessageCache.set(finalSessionId, [...cleaned, data.assistant_message]);
        } else {
          const without = cached.filter((x) => x.id !== optimistic.id && x.id !== streamId);
          _chatMessageCache.set(finalSessionId, [...without, { ...data.user_message, preview_image: attachedPreview }, data.assistant_message]);
        }
      } else {
        setActiveId(finalSessionId);
        setMessages((m) => {
          if (isContinuation) {
            const cleaned = m.filter((x) => x.id !== prevStoppedId && x.id !== streamId);
            return [...cleaned, data.assistant_message];
          }
          const without = m.filter((x) => x.id !== optimistic.id && x.id !== streamId);
          return [...without, { ...data.user_message, preview_image: attachedPreview }, data.assistant_message];
        });
      }
      if (wasNew) loadSessions();
      else {
        setSessions((s) => {
          const cp = [...s];
          const idx = cp.findIndex((x) => x.id === data.session_id);
          if (idx >= 0) { const [it] = cp.splice(idx, 1); cp.unshift({ ...it, updated_at: new Date().toISOString() }); }
          return cp;
        });
      }
    } catch (err) {
      // User pressed the Stop button → keep whatever tokens already
      // streamed in as the final assistant message so the conversation
      // stays coherent, silently swallow the AbortError, and let the
      // user type their next question.
      if (userStoppedRef.current || err?.name === "AbortError") {
        setMessages((m) => {
          // Freeze the streaming bubble: strip the _streaming flag and
          // append a small marker so the user sees it was stopped.
          return m.map((x) => {
            if (x.id !== streamId) return x;
            const content = (x.content || "").trim();
            const marker = "_" + (t("chat_stopped_marker") || "⏹ Stopped") + "._";
            return {
              ...x,
              _streaming: false,
              content: content ? content + "\n\n" + marker : marker,
            };
          });
        });
        userStoppedRef.current = false;
        return;
      }
      setMessages((m) => {
        if (isContinuation) {
          // Continuation network error: put the marker back on the prev
          // bubble and remove the empty streaming placeholder.
          const marker = "_" + (t("chat_stopped_marker") || "⏹ Stopped") + "._";
          return m
            .filter((x) => x.id !== streamId)
            .map((x) => {
              if (x.id !== prevStoppedId) return x;
              const content = (x.content || "").trim();
              return {
                ...x,
                _streaming: false,
                content: content ? content + "\n\n" + marker : marker,
              };
            });
        }
        return m.filter((x) => x.id !== optimistic.id && x.id !== streamId);
      });
      setInput(text);
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Free user hit daily chat limit → upgrade modal
      if (status === 429 && detail && typeof detail === "object" && detail.code === "chat_daily_limit") {
        setUpgradeMeta({ limit: detail.limit ?? 5, used: detail.used ?? 5 });
        setShowUpgrade(true);
      } else if (status === 429 && detail && typeof detail === "object" && detail.code === "gpt5_monthly_limit") {
        toast.error(detail.message || `GPT-5 monthly limit reached (${detail.limit}). Resets next month.`);
      } else if (status === 429 && detail && typeof detail === "object" && detail.code === "opus_monthly_limit") {
        // Auto-revert from Master to Smart so the next send doesn't 429 again
        setModel("claude-sonnet-4-6");
        toast.error(detail.message || `Master (Opus 4.5) monthly limit reached (${detail.limit}). Switched to Smart.`);
      } else if (status === 402 && detail && typeof detail === "object" && detail.code === "cv_mode_pro_only") {
        // CV mode tried by a Free / Creator user → switch back to text mode
        setMode("text");
        toast.error(detail.message || "CV chat is a Pro feature. Upgrade to unlock.");
      } else if (status === 402 && detail && typeof detail === "object" && detail.code === "travel_mode_pro_only") {
        // Travel mode tried by a Free / Creator user → switch back to text mode
        setMode("text");
        toast.error(detail.message || "Travel planner is a Pro feature. Upgrade to unlock.");
      } else if (status === 403 && detail && typeof detail === "object" && detail.code === "smart_router_pro_only") {
        // GPT-5 selected by a non-Pro user → revert to default model
        setModel(DEFAULT_MODEL);
        toast.error(detail.message || "GPT-5 is a Pro plan feature. Upgrade to unlock.");
      } else if (status === 403 && detail && typeof detail === "object" && detail.code === "model_tier_locked") {
        // Selected model requires higher tier → revert and toast (translated)
        const tierLabel = detail.required_tier === "pro" ? "Pro" : "Creator";
        const modelLabel = detail.model_key === "gpt-5" ? "GPT-5"
          : detail.model_key === "claude-opus-4-5-20251101" ? "Claude Opus 4.5"
          : detail.model_key === "claude-sonnet-4-6" ? "Claude Sonnet 4.6"
          : detail.model_key === "gpt-4o" ? "GPT-4o"
          : detail.model_key;
        setModel(DEFAULT_MODEL);
        toast.error(t("model_locked_toast", { model: modelLabel, tier: tierLabel }));
      } else if (detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : t("err_generic"));
      }
    } finally {
      setBusy(false);
      markSessionBusy(sendingSessionId, false);
      abortCtrlRef.current = null;
      streamingSessionRef.current = null;
    }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/chat/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      if (activeId === id) startNew();
    } catch { toast.error(t("err_generic")); }
  };

  const deleteAllSessions = async () => {
    if (sessions.length === 0) return;
    const ok = window.confirm(
      t("chat_delete_all_confirm") ||
        `Are you sure you want to delete ALL ${sessions.length} chats? This cannot be undone.`
    );
    if (!ok) return;
    try {
      const r = await api.delete("/chat/sessions");
      setSessions([]);
      startNew();
      toast.success(
        t("chat_delete_all_done", { n: r.data?.deleted ?? sessions.length }) ||
          `Deleted ${r.data?.deleted ?? sessions.length} chats`
      );
    } catch {
      toast.error(t("err_generic"));
    }
  };

  const copyMsg = (text) => { navigator.clipboard.writeText(text); toast.success(t("chat_copied")); };

  const downloadCVAsPDF = (markdown) => {
    if (!markdown) return;
    const ok = openCvForPrint(markdown, "CV", cvTemplateId);
    if (!ok) toast.error("Please allow pop-ups to download the CV");
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Block sending while the previous request is still in-flight — but
      // typing remains free so the user can prep their next message.
      if (busy) return;
      send();
    }
  };

  const suggestions = [t("chat_suggest_1"), t("chat_suggest_2"), t("chat_suggest_3"), t("chat_suggest_4")];

  return (
    <div
      data-testid="chat-page"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="relative flex-1 flex flex-col min-h-0"
    >
      {dragHover && (
        <div
          className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center bg-orange-500/15 backdrop-blur-sm"
          data-testid="chat-drop-overlay"
        >
          <div className="bg-white border-4 border-dashed border-orange-500 rounded-2xl px-10 py-8 text-center shadow-2xl">
            <ImageIcon className="w-12 h-12 text-orange-500 mx-auto" />
            <div className="mt-3 text-xl font-black tracking-tight">{t("chat_drop_title")}</div>
            <div className="mt-1 text-sm text-zinc-600">{t("chat_drop_sub")}</div>
          </div>
        </div>
      )}
      {showUpgrade && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="chat-upgrade-modal"
          onClick={() => setShowUpgrade(false)}
        >
          <div
            className="bg-white border-2 border-zinc-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-orange-500 to-pink-500 text-white px-6 py-5 relative">
              <button
                onClick={() => setShowUpgrade(false)}
                className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded"
                data-testid="upgrade-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                <Crown className="w-7 h-7" />
              </div>
              <h2 className="mt-3 text-2xl font-black tracking-tight">{t("chat_upgrade_title")}</h2>
              <p className="mt-1 text-sm text-white/90">
                {t("chat_upgrade_sub", { used: upgradeMeta.used, limit: upgradeMeta.limit })}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("chat_upgrade_benefits")}</div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {t("chat_upgrade_b1")}</li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {t("chat_upgrade_b2")}</li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {t("chat_upgrade_b3")}</li>
                <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /> {t("chat_upgrade_b4")}</li>
              </ul>

              <Link
                to="/app/billing"
                onClick={() => setShowUpgrade(false)}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3.5 rounded-md"
                data-testid="upgrade-modal-cta"
              >
                <Zap className="w-4 h-4" /> {t("chat_upgrade_cta")}
              </Link>
              <button
                onClick={() => setShowUpgrade(false)}
                className="mt-2 w-full text-center text-sm text-zinc-500 hover:text-zinc-900 py-2"
                data-testid="upgrade-modal-later"
              >
                {t("chat_upgrade_later")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="hidden md:flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("chat_eyebrow")}</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("chat_h1")}</h1>
          <p className="mt-2 text-zinc-600">{t("chat_sub")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap max-w-full">
          {/* Salesforce & Trailhead shortcuts — removed 2026-07-28
              alongside the pages themselves. Nothing renders here. */}
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("chat_speed") || t("chat_model")}</label>
          <ChatModelDropdown
            model={model}
            userPlan={userPlan}
            flagState={{
              quick:  flags?.chat_quick_model || "enabled",
              smart:  flags?.chat_smart_model || "enabled",
              genius: flags?.gpt5_smart_router || "enabled",
              master: flags?.opus_master || "enabled",
              sol:    flags?.gpt56_sol   || "enabled",
              terra:  flags?.gpt56_terra || "enabled",
              luna:   flags?.gpt56_luna  || "enabled",
            }}
            onChange={(nextModel, { locked, mode }) => {
              if (locked) {
                const tierLabel = mode.tier === "pro" ? "Pro" : "Creator";
                toast.error(
                  t("model_locked_toast", { model: mode.label, tier: tierLabel })
                );
                setShowUpgrade(true);
                return;
              }
              setModel(nextModel);
            }}
          />
          {/* Thinking-effort dial — Fast / Balanced / Thorough. Fast is
              the default and keeps latency + cost at baseline; Balanced
              and Thorough prepend a "think carefully" system-prompt hint
              on the backend so the answer becomes more considered. Works
              on every provider. Stored in localStorage so the user's
              choice sticks across sessions. */}
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 ml-2" data-testid="chat-thinking-label">
            {t("chat_thinking") || "Think"}
          </label>
          <select
            value={reasoningEffort}
            onChange={(e) => {
              setReasoningEffort(e.target.value);
              localStorage.setItem("lumioos_reasoning_effort", e.target.value);
            }}
            data-testid="chat-thinking-select"
            className="text-xs font-semibold bg-white border border-zinc-300 rounded-md px-2 py-1 hover:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900"
            title={t("chat_thinking_tooltip") || "How much should the model think before answering?"}
          >
            <option value="fast">{t("chat_thinking_fast") || "⚡ Fast"}</option>
            <option value="balanced">{t("chat_thinking_balanced") || "🎯 Balanced"}</option>
            <option value="thorough">{t("chat_thinking_thorough") || "🧠 Thorough"}</option>
          </select>
        </div>
      </div>

      {/* MOBILE ONLY — top tabs row + history drawer trigger + new chat.
          Full-width, sticky-ish; keeps the model tabs one tap away like
          ChatGPT while hiding the desktop H1/eyebrow that ate the fold. */}
      <div className="md:hidden -mx-5 sm:-mx-8 px-3 pt-2 pb-2 flex items-center gap-2 border-b border-zinc-200 bg-white sticky top-0 z-30">
        <Sheet open={mobileHistoryOpen} onOpenChange={setMobileHistoryOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 active:bg-zinc-100"
              data-testid="chat-mobile-history-btn"
              aria-label="Open chat history"
              title={t("chat_sessions") || "History"}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[300px]" data-testid="chat-mobile-history-drawer">
            <SheetHeader className="px-4 py-3 border-b border-zinc-200">
              <SheetTitle className="text-sm font-bold">{t("chat_sessions") || "Chats"}</SheetTitle>
            </SheetHeader>
            <div className="p-3 flex flex-col h-[calc(100vh-56px)]">
              <button
                onClick={() => { startNew(); setMobileHistoryOpen(false); }}
                data-testid="chat-mobile-new-btn"
                className="btn-accent w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md"
              >
                <Plus className="w-4 h-4" /> {t("chat_new")}
              </button>
              {sessions.length > 0 && (
                <div className="mt-3 flex items-center justify-between px-1">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                    {t("chat_sessions")}
                  </span>
                  <button
                    onClick={deleteAllSessions}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600 px-1.5 py-0.5 rounded hover:bg-red-50"
                    data-testid="chat-mobile-delete-all-btn"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t("chat_delete_all") || "Clear all"}
                  </button>
                </div>
              )}
              <div className="mt-2 flex-1 overflow-y-auto space-y-1">
                {sessions.length === 0 && (
                  <div className="text-xs text-zinc-400 px-1 py-2">{t("chat_no_sessions")}</div>
                )}
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => { setActiveId(s.id); setMobileHistoryOpen(false); }}
                    data-testid={`chat-mobile-session-${s.id}`}
                    className={`group cursor-pointer px-3 py-2 rounded-md text-sm border ${
                      activeId === s.id
                        ? "bg-zinc-950 text-white border-zinc-950"
                        : "border-transparent hover:border-zinc-200 hover:bg-zinc-50 text-zinc-800"
                    } flex items-center justify-between gap-2`}
                  >
                    <span className="truncate flex-1">{s.title}</span>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className={activeId === s.id ? "text-white/70 hover:text-white" : "text-zinc-400 hover:text-red-600"}
                      aria-label={t("chat_delete")}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0">
          <ChatModelDropdown
            className="w-full justify-between"
            model={model}
            userPlan={userPlan}
            flagState={{
              quick:  flags?.chat_quick_model || "enabled",
              smart:  flags?.chat_smart_model || "enabled",
              genius: flags?.gpt5_smart_router || "enabled",
              master: flags?.opus_master || "enabled",
              sol:    flags?.gpt56_sol   || "enabled",
              terra:  flags?.gpt56_terra || "enabled",
              luna:   flags?.gpt56_luna  || "enabled",
            }}
            onChange={(nextModel, { locked, mode: m }) => {
              if (locked) {
                const tierLabel = m.tier === "pro" ? "Pro" : "Creator";
                toast.error(t("model_locked_toast", { model: m.label, tier: tierLabel }));
                setShowUpgrade(true);
                return;
              }
              setModel(nextModel);
            }}
          />
        </div>
        <button
          onClick={startNew}
          data-testid="chat-mobile-new-topbar-btn"
          aria-label={t("chat_new")}
          className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md bg-gradient-to-br from-orange-500 to-pink-500 text-white hover:opacity-90 active:opacity-100"
          title={t("chat_new")}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* GPT-5 monthly usage meter — appears ONLY when GPT-5 is the active model */}
      {model === "gpt-5" && isPro && !isHidden("gpt5_smart_router") && gpt5Usage && gpt5Usage.limit !== 0 && (() => {
        const pct = gpt5Usage.limit > 0 ? Math.min(100, (gpt5Usage.used / gpt5Usage.limit) * 100) : 0;
        const isFull = gpt5Usage.limit > 0 && gpt5Usage.used >= gpt5Usage.limit;
        const isWarn = !isFull && pct >= 80;
        const next = new Date(); next.setMonth(next.getMonth() + 1); next.setDate(1);
        const renewStr = next.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        const themeCls = isFull
          ? "border-red-400 bg-gradient-to-r from-red-50 via-rose-50 to-red-50 text-red-900 shadow-red-100"
          : isWarn
            ? "border-orange-300 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 text-orange-900 shadow-orange-100"
            : "border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 text-amber-900 shadow-amber-100";
        const trackBg = isFull ? "bg-red-100" : isWarn ? "bg-orange-100" : "bg-amber-100";
        const barBg = isFull
          ? "bg-gradient-to-r from-red-500 via-rose-500 to-red-600"
          : isWarn
            ? "bg-gradient-to-r from-orange-400 via-amber-500 to-orange-500"
            : "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500";
        return (
          <div className={`mt-3 inline-flex flex-col gap-1.5 px-4 py-2.5 rounded-lg border shadow-sm ${themeCls}`} data-testid="gpt5-usage-meter" data-state={isFull ? "full" : isWarn ? "warn" : "ok"}>
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                <Crown className={`w-3.5 h-3.5 ${isFull ? "text-red-600" : isWarn ? "text-orange-600" : "text-amber-600"}`} />
                {t("chat_gpt5_meter_label") || "GPT-5 messages — this month"}
              </span>
              <span className="font-mono text-xs font-bold tabular-nums">
                {gpt5Usage.limit < 0 ? `${gpt5Usage.used}` : `${gpt5Usage.used} / ${gpt5Usage.limit}`}
              </span>
            </div>
            {gpt5Usage.limit > 0 && (
              <div className={`h-1.5 w-full rounded-full overflow-hidden ${trackBg}`}>
                <div className={`h-full transition-all duration-300 ${barBg}`} style={{ width: `${pct}%` }} />
              </div>
            )}
            {(isFull || isWarn) && (
              <div className="text-[10.5px] font-semibold mt-0.5 leading-tight">
                {isFull
                  ? `⚠ Limit reached — renews ${renewStr}`
                  : `⚠ ${gpt5Usage.limit - gpt5Usage.used} left this month`}
              </div>
            )}
          </div>
        );
      })()}

      {/* Master (Claude Opus 4.5) monthly usage meter — appears ONLY when Master is active */}
      {model === "claude-opus-4-5-20251101" && isPro && !isHidden("opus_master") && opusUsage && opusUsage.limit !== 0 && (() => {
        const pct = opusUsage.limit > 0 ? Math.min(100, (opusUsage.used / opusUsage.limit) * 100) : 0;
        const isFull = opusUsage.limit > 0 && opusUsage.used >= opusUsage.limit;
        const isWarn = !isFull && pct >= 80;
        const next = new Date(); next.setMonth(next.getMonth() + 1); next.setDate(1);
        const renewStr = next.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        const themeCls = isFull
          ? "border-red-400 bg-gradient-to-r from-red-50 via-rose-50 to-red-50 text-red-900 shadow-red-100"
          : isWarn
            ? "border-orange-300 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 text-orange-900 shadow-orange-100"
            : "border-fuchsia-300 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-purple-50 text-fuchsia-900 shadow-fuchsia-100";
        const trackBg = isFull ? "bg-red-100" : isWarn ? "bg-orange-100" : "bg-fuchsia-100";
        const barBg = isFull
          ? "bg-gradient-to-r from-red-500 via-rose-500 to-red-600"
          : isWarn
            ? "bg-gradient-to-r from-orange-400 via-amber-500 to-orange-500"
            : "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-600";
        return (
          <div className={`mt-3 inline-flex flex-col gap-1.5 px-4 py-2.5 rounded-lg border shadow-sm ${themeCls}`} data-testid="opus-usage-meter" data-state={isFull ? "full" : isWarn ? "warn" : "ok"}>
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                <Sparkles className={`w-3.5 h-3.5 ${isFull ? "text-red-600" : isWarn ? "text-orange-600" : "text-fuchsia-600"}`} />
                {t("chat_opus_meter_label") || "Master (Opus 4.5) messages — this month"}
              </span>
              <span className="font-mono text-xs font-bold tabular-nums">
                {opusUsage.limit < 0 ? `${opusUsage.used}` : `${opusUsage.used} / ${opusUsage.limit}`}
              </span>
            </div>
            {opusUsage.limit > 0 && (
              <div className={`h-1.5 w-full rounded-full overflow-hidden ${trackBg}`}>
                <div className={`h-full transition-all duration-300 ${barBg}`} style={{ width: `${pct}%` }} />
              </div>
            )}
            {(isFull || isWarn) && (
              <div className="text-[10.5px] font-semibold mt-0.5 leading-tight">
                {isFull
                  ? `⚠ Limit reached — renews ${renewStr}`
                  : `⚠ ${opusUsage.limit - opusUsage.used} left this month`}
              </div>
            )}
          </div>
        );
      })()}

      <div className="mt-3 md:mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-px bg-zinc-100 border-t border-zinc-200 flex-1 min-h-[600px] max-h-[calc(100dvh-180px)] -mx-4 sm:-mx-6 lg:-mx-8 -mb-6">
        {/* Sessions list — desktop only. Mobile users open the same list
            via the top-bar History drawer (Sheet). */}
        <div className="hidden lg:flex bg-white p-3 flex-col min-h-0 overflow-hidden" data-testid="chat-sessions">
          <button onClick={startNew} data-testid="chat-new-btn"
            className="btn-accent w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md">
            <Plus className="w-4 h-4" /> {t("chat_new")}
          </button>
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              {t("chat_sessions")}
            </span>
            {sessions.length > 0 && (
              <button
                onClick={deleteAllSessions}
                data-testid="chat-delete-all-btn"
                title={t("chat_delete_all") || "Delete all chats"}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-red-600 transition-colors px-1.5 py-0.5 rounded hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
                {t("chat_delete_all") || "Clear all"}
              </button>
            )}
          </div>
          <div className="mt-2 flex-1 overflow-y-auto space-y-1 max-h-[480px] lg:max-h-none">
            {sessions.length === 0 && (<div className="text-xs text-zinc-400 px-1 py-2">{t("chat_no_sessions")}</div>)}
            {sessions.map((s) => (
              <div key={s.id} onClick={() => setActiveId(s.id)} data-testid={`chat-session-${s.id}`}
                className={`group cursor-pointer px-3 py-2 rounded-md text-sm border ${
                  activeId === s.id ? "bg-zinc-950 text-white border-zinc-950" : "border-transparent hover:border-zinc-200 hover:bg-zinc-50 text-zinc-800"
                } flex items-center justify-between gap-2`}>
                <span className="truncate flex-1">{s.title}</span>
                <button onClick={(e) => deleteSession(s.id, e)}
                  className={`opacity-0 group-hover:opacity-100 ${activeId === s.id ? "text-white/70 hover:text-white" : "text-zinc-400 hover:text-red-600"}`}
                  aria-label={t("chat_delete")}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Message area — ChatGPT-style behavior:
            - When messages exist: scroller flex-1 fills, composer at bottom
            - When empty: chat-thread justify-center clusters title +
              composer + suggestions in the vertical middle of the workspace
              (like ChatGPT's "What should we work on?" screen). */}
        <div className={`bg-white flex flex-col min-h-0 ${messages.length === 0 && !loading ? "justify-center" : ""}`} data-testid="chat-thread">
          <div ref={scrollerRef} style={{ overflowAnchor: "none", scrollBehavior: "auto", WebkitOverflowScrolling: "touch" }} className={`${messages.length === 0 && !loading ? "flex-none" : "flex-1"} overflow-y-auto p-4 sm:p-6 md:p-8 min-h-0 relative`}>
            {loading ? (
              <div className="h-full flex items-center justify-center text-zinc-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-6">
                <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-3xl font-bold tracking-tight">{t("chat_empty_title")}</h3>
                <p className="mt-1 text-zinc-500 text-sm">{t("chat_empty_sub")}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((m) => {
                  // Last user message — passed to bubbles so the route-button
                  // logic can fall back to it for the "suggested" param when
                  // no blockquote is found in the AI response.
                  const lastUser = [...messages].reverse().find((x) => x.role === "user");
                  return (
                    <ChatMessageBubble
                      key={m.id}
                      m={m}
                      t={t}
                      cvTemplateId={cvTemplateId}
                      handleCvTemplateChange={handleCvTemplateChange}
                      downloadCVAsPDF={downloadCVAsPDF}
                      copyMsg={copyMsg}
                      routeButtonPrompt={lastUser?.content || ""}
                      webSearchActive={webSearch}
                      onEnableWebSearch={(prompt) => {
                        // One-click recovery from "I don't have live data":
                        // flip the toggle ON, drop any prior optimistic image
                        // prefixes (single or multi), and resend.
                        setWebSearch(true);
                        const clean = (prompt || "")
                          .replace(/^\[image attached\] /, "")
                          .replace(/^\[\d+ images attached\] /, "");
                        if (clean) send(clean);
                      }}
                    />
                  );
                })}
                {busy && !messages.some((m) => m._streaming) && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 rounded-lg inline-flex items-center gap-2 text-sm text-zinc-600">
                      <Loader2 className="w-4 h-4 animate-spin" /> {t("chat_thinking")}
                    </div>
                  </div>
                )}
                {/* Server-side pending indicator: shows when the user
                    returned to a chat session whose AI reply is still
                    running in the background (started from another tab).
                    The 2s message polling above auto-refreshes when the
                    reply lands, replacing this placeholder. */}
                {serverPendingThisSession && !busy && !messages.some((m) => m._streaming) && messages.length > 0 && messages[messages.length - 1].role === "user" && (
                  <div className="flex justify-start" data-testid="chat-bg-pending-bubble">
                    <div className="bg-white border-l-4 border-l-orange-500 border-y border-r border-zinc-200 px-4 py-3 rounded-lg inline-flex items-center gap-2.5 text-sm text-zinc-700 shadow-sm">
                      <span className="flex gap-0.5">
                        <span className="w-2 h-2 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-2 h-2 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: "240ms" }} />
                      </span>
                      <span className="font-semibold">{t("chat_bg_writing") || "AI is writing your reply…"}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Jump-to-bottom pill — sits above the composer and only appears
              while the user has scrolled up during a streaming answer. Tap
              re-glues the viewport to the bottom of the transcript. */}
          {showJumpToBottom && (
            <button
              type="button"
              onClick={() => scrollToBottom(true)}
              data-testid="chat-jump-to-bottom"
              className="absolute right-6 bottom-32 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-zinc-200 shadow-lg text-xs font-semibold text-zinc-700 hover:bg-white transition-colors"
              title={t("chat_jump_to_bottom") || "Jump to latest"}
            >
              <ChevronDown className="w-3.5 h-3.5" />
              <span>{t("chat_jump_to_bottom") || "Latest"}</span>
            </button>
          )}

          {/* Composer */}
          <div className="px-4 sm:px-6 pt-2 pb-4 bg-white">
            <div className="mx-auto w-full max-w-3xl">
            {mode === "cv" && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="chat-cv-upload-banner">
                {cvUploadName ? (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-sm text-blue-900 font-medium flex-1 truncate">{cvUploadName}</span>
                    <span className="text-xs text-blue-700">{t("cv_upload_ready") || "ready — type the job role you're applying for"}</span>
                    <button onClick={clearCvUpload} className="p-1 hover:bg-blue-100 rounded shrink-0" data-testid="chat-cv-upload-remove" aria-label="Remove">
                      <X className="w-3.5 h-3.5 text-blue-700" />
                    </button>
                  </div>
                ) : savedCv ? (
                  // Persistent CV on file — user doesn't need to re-upload.
                  // Shows filename + action buttons: Replace, Delete, PDF, ATS, Optimize.
                  <div className="flex flex-col gap-2" data-testid="chat-cv-saved-banner">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-sm text-emerald-900 font-semibold" data-testid="chat-cv-saved-label">
                        {t("cv_saved_label") || "Saved CV:"}
                      </span>
                      <span className="text-sm text-emerald-900 flex-1 truncate" data-testid="chat-cv-saved-filename">
                        {savedCv.filename}
                      </span>
                      <input
                        ref={cvFileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,image/*"
                        onChange={(e) => handleCvUploadFile(e.target.files?.[0])}
                        className="hidden"
                        data-testid="chat-cv-upload-input"
                      />
                      <button
                        onClick={() => cvFileInputRef.current?.click()}
                        disabled={cvUploadBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700 disabled:opacity-50"
                        data-testid="chat-cv-replace-btn"
                        title={t("cv_saved_replace") || "Replace"}
                      >
                        {cvUploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {t("cv_saved_replace") || "Replace"}
                      </button>
                      <button
                        onClick={deleteSavedCv}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-50 border border-transparent hover:border-red-200 text-xs font-semibold text-red-600"
                        data-testid="chat-cv-delete-btn"
                        title={t("cv_saved_delete") || "Delete"}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pl-6">
                      <button
                        onClick={downloadLatestCvAsPDF}
                        disabled={cvPdfBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid="chat-cv-pdf-btn"
                      >
                        {cvPdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {t("cv_pdf_btn") || "Download PDF"}
                      </button>
                      <button
                        onClick={runAtsScore}
                        disabled={cvPdfBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700 disabled:opacity-50"
                        data-testid="chat-cv-ats-btn"
                      >
                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                        {t("cv_ats_btn") || "ATS Score"}
                      </button>
                      <button
                        onClick={runOptimize}
                        disabled={cvPdfBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700 disabled:opacity-50"
                        data-testid="chat-cv-optimize-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                        {t("cv_optimize_btn") || "Optimize"}
                      </button>
                      <button
                        onClick={() => setCoverOpen(true)}
                        disabled={cvPdfBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700 disabled:opacity-50"
                        data-testid="chat-cv-cover-btn"
                      >
                        <Mail className="w-3.5 h-3.5 text-rose-500" />
                        {t("cv_cover_btn") || "Cover Letter"}
                      </button>
                      <button
                        onClick={openCvHistory}
                        disabled={cvPdfBusy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700 disabled:opacity-50"
                        data-testid="chat-cv-history-btn"
                      >
                        <History className="w-3.5 h-3.5 text-blue-500" />
                        {t("cv_history_btn") || "History"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-blue-900">{t("cv_upload_title") || "Upload your CV (optional)"}</div>
                      <div className="text-xs text-blue-700">{t("cv_upload_hint") || "PDF, Word, TXT or image — Lumioos will review it and tailor it to the job you're applying for."}</div>
                    </div>
                    <input
                      ref={cvFileInputRef}
                      type="file"
                      accept=".pdf,.docx,.txt,image/*"
                      onChange={(e) => handleCvUploadFile(e.target.files?.[0])}
                      className="hidden"
                      data-testid="chat-cv-upload-input"
                    />
                    <button
                      onClick={() => cvFileInputRef.current?.click()}
                      disabled={cvUploadBusy || savedCvLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50 shrink-0"
                      data-testid="chat-cv-upload-btn"
                    >
                      {cvUploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {t("cv_upload_btn") || "Upload CV"}
                    </button>
                  </div>
                )}
              </div>
            )}
            {images.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="chat-image-chip">
                {images.map((im, idx) => (
                  <div
                    key={idx}
                    className="relative inline-flex items-center gap-2 p-1.5 pr-2 bg-white border border-zinc-200 rounded-md"
                  >
                    <img src={im.preview} alt={`preview-${idx}`} className="w-10 h-10 object-cover rounded" />
                    <button
                      onClick={() => removeImageAt(idx)}
                      className="p-1 hover:bg-zinc-100 rounded"
                      data-testid={`chat-image-remove-${idx}`}
                      aria-label={t("chat_image_remove")}
                    >
                      <X className="w-3.5 h-3.5 text-zinc-500" />
                    </button>
                  </div>
                ))}
                <span className="text-xs text-zinc-500">{images.length}/{MAX_IMAGES_PER_TURN}</span>
              </div>
            )}
            <div className="flex flex-col gap-2 rounded-2xl border border-zinc-200 bg-white shadow-sm p-2 focus-within:border-zinc-400 focus-within:shadow-md transition-all">
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onImagePick} className="hidden" data-testid="chat-file-input" />
              {/* Top row: textarea + send button */}
              <div className="flex items-end gap-2">
                <textarea data-testid="chat-input" rows={2} value={input}
                  onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} onPaste={onPasteFromClipboard}
                  placeholder={
                    mode === "image" ? (t("chat_image_placeholder") || "Describe the image you want…")
                    : mode === "code" ? (t("chat_code_placeholder") || "Describe the code you need…")
                    : mode === "cv" ? (t("chat_cv_placeholder") || "Start with: 'I'm John, I want a CV for a Senior Designer role'")
                    : mode === "video" ? "Video mode coming soon — type your idea anyway"
                    : (recording ? t("chat_recording_ph") : t("chat_placeholder"))
                  }
                  className="flex-1 px-3 py-2 bg-transparent focus:outline-none resize-none text-sm border-0" />
                {busy ? (
                  <button onClick={stopStreaming} data-testid="chat-stop-btn" title={t("chat_stop") || "Stop"}
                    className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors">
                    <Square className="w-3.5 h-3.5 fill-current" />
                    <span className="hidden sm:inline">{t("chat_stop") || "Stop"}</span>
                  </button>
                ) : (() => {
                  const lastMsg = messages[messages.length - 1];
                  const wasStopped = lastMsg && lastMsg.role === "assistant" && typeof lastMsg.content === "string" && /\[stopped by user\]|⏹ Durduruldu|⏹ Stopped|⏹ Detenido|⏹ Arrêté|⏹ Gestoppt|⏹ Fermato/.test(lastMsg.content);
                  return (
                    <>
                      {wasStopped && (
                        <button onClick={continueStreaming} data-testid="chat-continue-btn" title={t("chat_continue") || "Continue"}
                          className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 transition-colors">
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                          <span className="hidden sm:inline">{t("chat_continue") || "Continue"}</span>
                        </button>
                      )}
                      <button onClick={() => send()} disabled={!input.trim() && images.length === 0} data-testid="chat-send-btn"
                        className="btn-accent inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-md disabled:opacity-50">
                        <Send className="w-4 h-4" />
                        <span className="hidden sm:inline">{t("chat_send")}</span>
                      </button>
                    </>
                  );
                })()}
              </div>
              {/* Bottom row: mode toolbar */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || (mode !== "text" && mode !== "cv") || images.length >= MAX_IMAGES_PER_TURN}
                  title={
                    images.length >= MAX_IMAGES_PER_TURN
                      ? (t("chat_image_max") || `Max ${MAX_IMAGES_PER_TURN} images per message`)
                      : t("chat_attach_image")
                  }
                  data-testid="chat-attach-btn"
                  className="shrink-0 w-9 h-9 inline-flex items-center justify-center border border-zinc-300 bg-white rounded-md hover:border-blue-600 disabled:opacity-50">
                  <ImageIcon className="w-3.5 h-3.5 text-zinc-700" />
                </button>
                <button onClick={recording ? stopRecording : startRecording} disabled={busy || transcribing}
                  title={recording ? t("chat_stop_recording") : t("chat_start_recording")}
                  data-testid="chat-mic-btn"
                  className={`shrink-0 w-9 h-9 inline-flex items-center justify-center border rounded-md disabled:opacity-50 ${
                    recording ? "bg-red-600 text-white border-red-600 animate-pulse" : "bg-white border-zinc-300 hover:border-blue-600"
                  }`}>
                  {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => setWebSearch(w => !w)}
                  disabled={busy}
                  title={webSearch ? t("chat_websearch_on") : t("chat_websearch_off")}
                  data-testid="chat-websearch-btn"
                  className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                    webSearch
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white border-zinc-300 text-zinc-700 hover:border-blue-600"
                  }`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{t("chat_websearch_label")}</span>
                </button>
                <div className="h-6 w-px bg-zinc-200 mx-1" />
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <button
                    onClick={() => setMode(m => m === "image" ? "text" : "image")}
                    disabled={busy}
                    data-testid="chat-mode-image"
                    title={mode === "image" ? "Image mode ON" : "Generate image"}
                    className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                      mode === "image"
                        ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white border-transparent shadow-sm"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-orange-500 hover:text-orange-600"
                    }`}>
                    <Sparkles className="w-3.5 h-3.5" /> Image
                  </button>
                  <span className="text-[9px] font-medium text-zinc-500 uppercase tracking-wider leading-none">{t("chat_mode_image_hint")}</span>
                </div>
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <button
                    onClick={() => setMode(m => m === "code" ? "text" : "code")}
                    disabled={busy}
                    data-testid="chat-mode-code"
                    title={mode === "code" ? "Code mode ON" : "Code mode"}
                    className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                      mode === "code"
                        ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white border-transparent shadow-sm"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-emerald-500 hover:text-emerald-600"
                    }`}>
                    <Code2 className="w-3.5 h-3.5" /> Code
                  </button>
                  <span className="text-[9px] font-medium text-zinc-500 uppercase tracking-wider leading-none">{t("chat_mode_code_hint")}</span>
                </div>
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  {(() => {
                    const isCvAllowed = user?.is_admin || userPlan === "pro" || userPlan === "power" || userPlan === "admin" || user?.is_trial;
                    return (
                      <button
                        onClick={() => {
                          if (!isCvAllowed && mode !== "cv") {
                            toast.error(t("cv_mode_pro_only") || "CV chat is a Pro feature. Upgrade to unlock.");
                            navigate("/app/billing");
                            return;
                          }
                          setMode(m => m === "cv" ? "text" : "cv");
                        }}
                        disabled={busy}
                        data-testid="chat-mode-cv"
                        title={!isCvAllowed ? (t("cv_mode_pro_only_short") || "CV — Pro feature") : (mode === "cv" ? "CV mode ON" : "Build CV")}
                        className={`relative shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                          mode === "cv"
                            ? "bg-gradient-to-br from-blue-500 to-indigo-500 text-white border-transparent shadow-sm"
                            : !isCvAllowed
                              ? "bg-zinc-50 border-zinc-200 text-zinc-400 cursor-pointer hover:border-amber-400 hover:text-amber-600"
                              : "bg-white border-zinc-300 text-zinc-700 hover:border-blue-500 hover:text-blue-600"
                        }`}>
                        {!isCvAllowed ? <Lock className="w-3 h-3" /> : <FileText className="w-3.5 h-3.5" />}
                        CV
                        {!isCvAllowed && (
                          <span className="absolute -top-1.5 -right-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-400 text-amber-950">PRO</span>
                        )}
                      </button>
                    );
                  })()}
                  <span className="text-[9px] font-medium text-zinc-500 uppercase tracking-wider leading-none">{t("chat_mode_cv_hint")}</span>
                </div>
                {/* Travel planner mode — toggles a structured itinerary
                    system prompt and forces Tavily web search ON so flight
                    and hotel prices reflect actual current ranges.
                    Pro/Power-only because it burns paid Tavily calls + Sonnet 4.6. */}
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  {(() => {
                    const isTravelAllowed = user?.is_admin || userPlan === "pro" || userPlan === "power" || userPlan === "admin" || user?.is_trial;
                    return (
                      <button
                        onClick={() => {
                          if (!isTravelAllowed && mode !== "travel") {
                            toast.error(t("travel_mode_pro_only") || "Travel planner is a Pro feature. Upgrade to unlock.");
                            navigate("/app/billing");
                            return;
                          }
                          setMode(m => m === "travel" ? "text" : "travel");
                        }}
                        disabled={busy}
                        data-testid="chat-mode-travel"
                        title={!isTravelAllowed ? (t("travel_mode_pro_only_short") || "Travel — Pro feature") : (mode === "travel" ? "Travel mode ON" : "Travel planner")}
                        className={`relative shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                          mode === "travel"
                            ? "bg-gradient-to-br from-cyan-500 to-teal-500 text-white border-transparent shadow-sm"
                            : !isTravelAllowed
                              ? "bg-zinc-50 border-zinc-200 text-zinc-400 cursor-pointer hover:border-amber-400 hover:text-amber-600"
                              : "bg-white border-zinc-300 text-zinc-700 hover:border-cyan-500 hover:text-cyan-600"
                        }`}>
                        {!isTravelAllowed ? <Lock className="w-3 h-3" /> : <Plane className="w-3.5 h-3.5" />}
                        {t("chat_mode_travel") || "Travel"}
                        {!isTravelAllowed && (
                          <span className="absolute -top-1.5 -right-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-400 text-amber-950">PRO</span>
                        )}
                      </button>
                    );
                  })()}
                  <span className="text-[9px] font-medium text-zinc-500 uppercase tracking-wider leading-none">{t("chat_mode_travel_hint") || "trip planner"}</span>
                </div>
                {!videoHidden && (
                <div className="shrink-0 flex flex-col items-center gap-0.5">
                  <button
                    onClick={() => setMode(m => m === "video" ? "text" : "video")}
                    disabled={busy}
                    data-testid="chat-mode-video"
                    title={videoComingSoon ? "Video mode (coming soon)" : "Video mode"}
                    className={`relative shrink-0 inline-flex items-center gap-1.5 h-9 px-3 border rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                      mode === "video"
                        ? "bg-gradient-to-br from-pink-500 to-fuchsia-500 text-white border-transparent shadow-sm"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-pink-500 hover:text-pink-600"
                    }`}>
                    <Clapperboard className="w-3.5 h-3.5" /> Video
                    {videoComingSoon && (
                      <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold bg-amber-500 text-white px-1 rounded-sm leading-tight">SOON</span>
                    )}
                  </button>
                  <span className="text-[9px] font-medium text-zinc-500 uppercase tracking-wider leading-none">{t("chat_mode_video_hint")}</span>
                </div>
                )}
                {mode !== "text" && (
                  <button
                    onClick={() => setMode("text")}
                    data-testid="chat-mode-reset"
                    className="ml-auto text-[11px] font-semibold text-zinc-500 hover:text-zinc-900"
                  >
                    Reset to chat
                  </button>
                )}
              </div>
            </div>
            </div>
          </div>
          {/* ChatGPT-style suggestion chips — rendered BELOW the composer
              only in the empty state so the composer sits close to the
              welcome title and suggestions form a compact "quick start"
              tray underneath. */}
          {messages.length === 0 && !loading && (
            <div className="px-4 sm:px-6 pb-8 pt-2">
              <div className="mx-auto max-w-3xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {suggestions.slice(0, 6).map((sg, i) => {
                  // Round-robin an accent icon so the row cards look
                  // visually varied like ChatGPT's featured suggestions.
                  const icons = [MessageSquare, Code2, Plane, FileText, Globe, Sparkles];
                  const tints = ["text-emerald-600","text-blue-600","text-orange-600","text-fuchsia-600","text-cyan-600","text-amber-600"];
                  const Icon = icons[i % icons.length];
                  const tint = tints[i % tints.length];
                  return (
                    <button key={i} onClick={() => send(sg)} data-testid={`chat-suggestion-${i}`}
                      className="group flex items-start gap-3 text-left p-3 rounded-xl border border-zinc-200 bg-white hover:border-zinc-400 hover:shadow-sm transition-all">
                      <div className={`shrink-0 w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center ${tint} group-hover:bg-zinc-100`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1 text-sm text-zinc-800 leading-snug">
                        {sg}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CV Version History dialog — lightweight modal showing up to 5
          past CVs with previews, restore + PDF actions. Uses the existing
          Sheet component so it renders as a right-side drawer on desktop
          and covers on mobile. */}
      <Sheet open={cvHistoryOpen} onOpenChange={setCvHistoryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="cv-history-drawer">
          <SheetHeader>
            <SheetTitle>{t("cv_history_title") || "CV Version History"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {cvHistoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
              </div>
            ) : cvHistoryVersions.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">{t("cv_history_empty") || "No CVs saved yet."}</p>
            ) : (
              cvHistoryVersions.map((v) => (
                <div
                  key={v.index}
                  data-testid={`cv-history-item-${v.index}`}
                  className={`rounded-lg border p-3 ${v.current ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className={`w-4 h-4 ${v.current ? "text-emerald-600" : "text-zinc-500"}`} />
                    <span className="text-sm font-semibold text-zinc-900 truncate flex-1">{v.filename || `v${v.index}`}</span>
                    {v.current && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white font-semibold uppercase tracking-wide" data-testid="cv-history-current-badge">
                        {t("cv_history_current") || "Active"}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 mb-2">
                    {v.saved_at ? new Date(v.saved_at).toLocaleString() : ""} · {v.char_count} chars
                  </div>
                  <div className="text-xs text-zinc-700 whitespace-pre-line line-clamp-4 mb-2">{v.preview}</div>
                  {!v.current && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => restoreCvVersion(v.index)}
                        disabled={cvRestoring !== null}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                        data-testid={`cv-history-restore-${v.index}`}
                      >
                        {cvRestoring === v.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        {t("cv_history_restore") || "Restore"}
                      </button>
                      <button
                        onClick={() => openDiff(v.index)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700"
                        data-testid={`cv-history-diff-${v.index}`}
                      >
                        <FileText className="w-3.5 h-3.5 text-amber-600" />
                        {t("cv_diff_btn") || "Diff"}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* AI Cover Letter dialog — richer than a native window.prompt so we
          can collect JD + optional company/role/tone in one shot. */}
      <Sheet open={coverOpen} onOpenChange={setCoverOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="cv-cover-drawer">
          <SheetHeader>
            <SheetTitle>{t("cv_cover_title") || "AI Cover Letter"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-zinc-700 block mb-1">
                {t("cv_cover_jd_label") || "Job description"} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={coverJd}
                onChange={(e) => setCoverJd(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t("cv_cover_jd_placeholder") || "Paste the full job description here…"}
                data-testid="cv-cover-jd-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-zinc-700 block mb-1">{t("cv_cover_company_label") || "Company"}</label>
                <input
                  value={coverCompany}
                  onChange={(e) => setCoverCompany(e.target.value)}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Inc."
                  data-testid="cv-cover-company-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-700 block mb-1">{t("cv_cover_role_label") || "Role"}</label>
                <input
                  value={coverRole}
                  onChange={(e) => setCoverRole(e.target.value)}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Senior Engineer"
                  data-testid="cv-cover-role-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-700 block mb-1">{t("cv_cover_tone_label") || "Tone"}</label>
              <select
                value={coverTone}
                onChange={(e) => setCoverTone(e.target.value)}
                className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="cv-cover-tone-select"
              >
                <option value="confident">{t("cv_cover_tone_confident") || "Confident & professional"}</option>
                <option value="warm">{t("cv_cover_tone_warm") || "Warm & friendly"}</option>
                <option value="formal">{t("cv_cover_tone_formal") || "Formal"}</option>
                <option value="enthusiastic">{t("cv_cover_tone_enthusiastic") || "Enthusiastic"}</option>
              </select>
            </div>
            <button
              onClick={runCoverLetter}
              disabled={coverBusy || coverJd.trim().length < 20}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="cv-cover-generate-btn"
            >
              {coverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {coverLastMd ? (t("cv_cover_regenerate") || "Regenerate") : (t("cv_cover_generate") || "Generate Cover Letter")}
            </button>
            {coverLastMd && (
              // Success panel — shown after the first successful generation.
              // Persists so the user can grab a PDF on demand without having
              // to re-generate the letter.
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="cv-cover-success-panel">
                <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {t("cv_cover_success") || "Cover letter added to your chat."}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={downloadCoverLetterPdf}
                    disabled={coverPdfBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                    data-testid="cv-cover-pdf-btn"
                  >
                    {coverPdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {t("cv_cover_pdf_btn") || "Download as PDF"}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(coverLastMd); toast.success(t("copied") || "Copied"); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700"
                    data-testid="cv-cover-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {t("cv_cover_copy_btn") || "Copy"}
                  </button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-zinc-500">{t("cv_cover_hint") || "Uses your saved CV — no re-upload needed."}</p>
          </div>
        </SheetContent>
      </Sheet>

      {/* CV Diff View — full-screen dialog with side-by-side (desktop) or
          stacked (mobile) columns showing added/removed/unchanged lines
          between the current CV and a historical version. */}
      <Sheet open={diffOpen} onOpenChange={setDiffOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="cv-diff-drawer">
          <SheetHeader>
            <SheetTitle>{t("cv_diff_title") || "CV Version Diff"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-mono truncate max-w-[45%]" data-testid="cv-diff-from-label" title={diffFromLabel}>
                − {diffFromLabel}
              </span>
              <span className="text-zinc-400">→</span>
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-mono truncate max-w-[45%]" data-testid="cv-diff-to-label" title={diffToLabel}>
                + {diffToLabel}
              </span>
            </div>
            {diffLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
              </div>
            ) : diffBlocks.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">{t("cv_diff_empty") || "No changes."}</p>
            ) : (
              <div className="rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden" data-testid="cv-diff-body">
                {diffBlocks.map((b, i) => {
                  const bg = b.added ? "bg-emerald-50" : b.removed ? "bg-red-50" : "bg-white";
                  const border = b.added ? "border-l-2 border-emerald-500" : b.removed ? "border-l-2 border-red-500" : "border-l-2 border-transparent";
                  const prefix = b.added ? "+ " : b.removed ? "− " : "  ";
                  return (
                    <pre
                      key={i}
                      className={`whitespace-pre-wrap font-mono text-xs px-3 py-1 ${bg} ${border}`}
                    >
                      {b.value.split("\n").filter((l, idx, arr) => !(idx === arr.length - 1 && l === "")).map((ln, li) => (
                        <span key={li} className="block">
                          <span className={b.added ? "text-emerald-700" : b.removed ? "text-red-700" : "text-zinc-500"}>{prefix}</span>
                          <span className="text-zinc-800">{ln}</span>
                        </span>
                      ))}
                    </pre>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => { setDiffOpen(false); if (diffToIdx) restoreCvVersion(diffToIdx); }}
                disabled={diffLoading || cvRestoring !== null || !diffToIdx}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                data-testid="cv-diff-restore-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {t("cv_diff_restore_this") || "Restore this version"}
              </button>
              <button
                onClick={() => setDiffOpen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white hover:bg-zinc-50 border border-zinc-200 text-xs font-semibold text-zinc-700"
                data-testid="cv-diff-close-btn"
              >
                {t("cv_diff_close") || "Close"}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
